import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, ChevronRightIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/solid';

const PREVIEW_SIZE = 240;

export function getStickyNoteImages(note) {
  if (Array.isArray(note?.images) && note.images.length > 0) return note.images;
  return note?.imageUrl ? [note.imageUrl] : [];
}

/** Fixed-position spot for the hover preview: left of the thumbnail, else right of it. */
export function computePreviewPosition(rect, viewport, size = PREVIEW_SIZE) {
  const gap = 12;
  let left = rect.left - size - gap;
  if (left < 8) left = Math.min(rect.right + gap, viewport.width - size - 8);
  const top = Math.max(8, Math.min(rect.top + rect.height / 2 - size / 2, viewport.height - size - 8));
  return { left: Math.max(8, left), top };
}

function canHover() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * Sticky images for the open page: a compact thumbnail grid. Click opens the
 * gallery; on desktop, hovering a thumbnail shows a larger preview.
 */
export default function StickyImagesPanel({
  notes,
  collapsed,
  onToggleCollapsed,
  highlightedId,
  onOpen,
  onDelete,
}) {
  const list = Array.isArray(notes) ? notes : [];
  const [preview, setPreview] = useState(null);
  const [failed, setFailed] = useState(() => new Set());

  const showPreview = (event, src) => {
    if (!src || !canHover()) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPreview({ src, ...computePreviewPosition(rect, { width: window.innerWidth, height: window.innerHeight }) });
  };
  const hidePreview = () => setPreview(null);

  return (
    <div
      className={`${collapsed ? 'ml-auto w-10 p-1.5' : 'w-full p-2.5 lg:w-56'} mt-4 flex shrink-0 flex-col self-start overflow-hidden rounded-card border border-[#E5E7EB] bg-white dark:border-[#374151] dark:bg-[#1F2937] lg:mr-4 lg:mt-0`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onToggleCollapsed}
        className={`flex items-center gap-1.5 rounded text-[#6B7280] hover:text-[#111827] dark:text-[#9CA3AF] dark:hover:text-[#F9FAFB] ${collapsed ? 'flex-col py-1' : 'mb-2 px-0.5'}`}
        title={collapsed ? 'Expand Sticky Notes' : 'Collapse Sticky Notes'}
        aria-expanded={!collapsed}
      >
        <PhotoIcon className="h-4 w-4 shrink-0" />
        {collapsed ? (
          <span className="text-[11px] tabular-nums">{list.length}</span>
        ) : (
          <>
            <span className="text-xs font-semibold uppercase tracking-wide">Sticky notes</span>
            <span className="text-xs tabular-nums">{list.length}</span>
            <ChevronDownIcon className="ml-auto h-3.5 w-3.5" />
          </>
        )}
        {collapsed ? <ChevronRightIcon className="h-3 w-3" /> : null}
      </button>

      {!collapsed && (
        list.length > 0 ? (
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-8 lg:grid-cols-3">
            {list.map((note) => {
              const images = getStickyNoteImages(note);
              const first = images[0];
              const isHighlighted = highlightedId === note.id;
              const label = note.selectedText ? `"${note.selectedText}"` : 'Page note';
              return (
                <div key={note.id} className="group relative" data-sticky-id={note.id}>
                  <button
                    type="button"
                    onClick={() => { hidePreview(); onOpen(note); }}
                    onMouseEnter={(e) => showPreview(e, first)}
                    onMouseLeave={hidePreview}
                    onFocus={hidePreview}
                    className={`relative block aspect-square w-full overflow-hidden rounded-md bg-[#F3F4F6] ring-1 ring-black/5 transition-shadow dark:bg-[#111827] dark:ring-white/10 ${
                      isHighlighted ? '!ring-2 !ring-[#3B82F6]' : 'hover:ring-[#9CA3AF] dark:hover:ring-[#6B7280]'
                    }`}
                    title={`${label} (${images.length} image${images.length === 1 ? '' : 's'})`}
                    aria-label={`Open sticky note for ${label}, ${images.length} image${images.length === 1 ? '' : 's'}`}
                  >
                    {first && !failed.has(note.id) ? (
                      <img
                        src={first}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={() => setFailed((prev) => new Set(prev).add(note.id))}
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center">
                        <PhotoIcon className="h-4 w-4 text-[#9CA3AF]" />
                      </span>
                    )}
                    {images.length > 1 && (
                      <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 text-[10px] font-medium leading-4 text-white">
                        {images.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); hidePreview(); onDelete(note); }}
                    className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#111827]/80 text-white opacity-0 transition-opacity hover:bg-[#EF4444] focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                    title="Delete sticky note"
                    aria-label={`Delete sticky note for ${label}`}
                  >
                    <XMarkIcon className="h-2.5 w-2.5" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="px-0.5 pb-1 text-xs leading-5 text-[#6B7280] dark:text-[#9CA3AF]">
            Select text and add an image, or paste / drop one onto the page.
          </p>
        )
      )}

      {preview && typeof document !== 'undefined' && createPortal(
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-[200] overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-elevated dark:border-[#374151] dark:bg-[#111827]"
          style={{ left: preview.left, top: preview.top, width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
        >
          <img src={preview.src} alt="" className="h-full w-full object-contain" />
        </div>,
        document.body
      )}
    </div>
  );
}
